/**
 * StatusBanner Component — Soorgaa Design System
 * 
 * Persistent status banner for inline feedback, errors, or offline alerts.
 * Uses strict tokens and accessible color-mix variants.
 */
import React from "react";
import { CheckCircle2, AlertOctagon, AlertTriangle, Info, X } from "lucide-react";
import { Button } from "./Button";

export type StatusType = "success" | "error" | "warning" | "info";

export interface StatusBannerProps {
  type?: StatusType;
  title?: string;
  message: React.ReactNode;
  onClose?: () => void;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function StatusBanner({
  type = "info",
  title,
  message,
  onClose,
  className = "",
  action,
}: StatusBannerProps) {
  const config = {
    success: {
      icon: CheckCircle2,
      container: "bg-success/10 border-success/30 text-foreground border-l-2 border-l-success",
      iconColor: "text-success",
    },
    error: {
      icon: AlertOctagon,
      container: "bg-destructive/10 border-destructive/30 text-foreground border-l-2 border-l-destructive",
      iconColor: "text-destructive",
    },
    warning: {
      icon: AlertTriangle,
      container: "bg-warning/10 border-warning/30 text-foreground border-l-2 border-l-warning",
      iconColor: "text-warning",
    },
    info: {
      icon: Info,
      container: "bg-info/10 border-info/30 text-foreground border-l-2 border-l-info",
      iconColor: "text-info",
    },
  }[type];

  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={`relative rounded-md border px-3.5 py-3 text-xs sm:text-sm shadow-xs transition-all flex items-start gap-3 ${config.container} ${className}`.trim()}
    >
      <Icon size={18} className={`shrink-0 mt-0.5 ${config.iconColor}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold mb-0.5 leading-snug">{title}</p>}
        <div className="leading-relaxed opacity-95">{message}</div>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-2 text-xs font-semibold text-primary underline underline-offset-2 hover:opacity-80 block cursor-pointer"
          >
            {action.label}
          </button>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 p-1 rounded-xs hover:bg-black/5 dark:hover:bg-white/5 opacity-70 hover:opacity-100 transition cursor-pointer"
          aria-label="Close notification"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Modern state card for failed/empty/warning states with sharp borders and clean styling */
export function ModernStateCard({
  type = "info",
  title,
  description,
  action,
}: {
  type?: StatusType;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}) {
  const config = {
    success: {
      icon: CheckCircle2,
      iconBg: "bg-success/10 text-success border-success/20",
    },
    error: {
      icon: AlertOctagon,
      iconBg: "bg-destructive/10 text-destructive border-destructive/20",
    },
    warning: {
      icon: AlertTriangle,
      iconBg: "bg-warning/10 text-warning border-warning/20",
    },
    info: {
      icon: Info,
      iconBg: "bg-info/10 text-info border-info/20",
    },
  }[type];

  const Icon = config.icon;

  return (
    <div className="surface-sm p-6 text-center rounded-lg max-w-md mx-auto border border-border">
      <div className={`w-11 h-11 mx-auto rounded-md border flex items-center justify-center mb-3 ${config.iconBg}`}>
        <Icon size={20} aria-hidden="true" />
      </div>
      <h3 className="font-display text-base font-bold text-foreground mb-1">{title}</h3>
      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
        {description}
      </p>
      {action && (
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
